<template>
  <div class="display">
    <div v-if="validInputs" class="display-box">
      <div class="top-row">
        <div class="top-left"><b> {{ isPressure ? getCalcData.pressureDifference : getCalcData.flowDifference }}</b></div>
        <div class="top-middle" id="title" :title="title">
          <div class="top-middle-icon" :class="(isPressure) ? 'pressure-label' : 'flow-label'">
            <b>&Delta;{{ isPressure ? 'P' : 'Q' }}</b>
          </div>
          <div class="top-middle-unit" :class="(isPressure) ? 'pressure-color' : 'flow-color'">{{ unit }}</div>
        </div>
        <div class="top-right"><b><span v-if="this.$props.type === 'pressure'">{{ getCalcData.pressureVariation }}%</span><span v-else>{{ getCalcData.flowVariation }}%</span></b></div>
      </div>
      <div class="bottom-row">
        <div class="bottom-cell bottom-left">
          <div class="label"><b>AVG</b></div>
          <div :class="(isPressure) ? 'pressure-value' : 'flow-value'">{{ getAvg }}</div>
        </div>
        <div class="bottom-cell bottom-middle">
          <div class="label"><b>MAX</b></div>
          <div :class="(isPressure) ? 'pressure-value' : 'flow-value'">{{ getMax }}</div>
        </div>
        <div class="bottom-cell bottom-right">
          <div class="label"><b>MIN</b></div>
          <div :class="(isPressure) ? 'pressure-value' : 'flow-value'">{{ getMin }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as UnitConversion from '@/services/UnitConversionService.js';
import { useInputStore } from '@/store/InputStore';
import { useSettingsStore } from '@/store/SettingsStore';

export default {
  props: [
    'type',
  ],
  computed: {
    validInputs () {
      return useInputStore().validInputs;
    },
    unit () {
      return this.isPressure ? this.pressureUnit.toUpperCase() : this.flowUnit.toUpperCase();
    },
    getCalcData () {
      const calcData = useInputStore().getCalcData;
      let obj = {};
      obj.pressureDifference = this.filterPressure(calcData.pressureDifference);
      obj.flowDifference = this.filterEmitterFlow(calcData.flowDifference);
      if (isNaN(calcData.pressureVariation)) {
        obj.pressureVariation = 'N/A';
      } else {
        obj.pressureVariation = JSON.parse(calcData.pressureVariation).toFixed(2);
      }
      obj.flowVariation = JSON.parse(calcData.flowVariation).toFixed(2);
      if (isNaN(calcData.averageSprinklerPressure)) {
        obj.averageSprinklerPressure = 'N/A';
      } else {
        obj.averageSprinklerPressure = this.filterPressure(calcData.averageSprinklerPressure);
      }
      if (isNaN(calcData.maxSprinklerPressure)) {
        obj.maxSprinklerPressure = 'N/A';
      } else {
        obj.maxSprinklerPressure = this.filterPressure(calcData.maxSprinklerPressure);
      }
      if (isNaN(calcData.minSprinklerPressure)) {
        obj.minSprinklerPressure = 'N/A';
      } else {
        obj.minSprinklerPressure = this.filterPressure(calcData.minSprinklerPressure);
      }
      obj.averageNozFlow = this.filterEmitterFlow(calcData.averageNozFlow);
      obj.maxNozFlow = this.filterEmitterFlow(calcData.maxNozFlow);
      obj.minNozFlow = this.filterEmitterFlow(calcData.minNozFlow);
      useInputStore().setFormattedPipeData(obj);
      return obj;
    },
    getAvg () {
      return this.isPressure ? this.getCalcData.averageSprinklerPressure : this.getCalcData.averageNozFlow;
    },
    getMax () {
      return this.isPressure ? this.getCalcData.maxSprinklerPressure : this.getCalcData.maxNozFlow;
    },
    getMin () {
      return this.isPressure ? this.getCalcData.minSprinklerPressure : this.getCalcData.minNozFlow;
    },
    isPressure () {
      return this.$props.type == 'pressure';
    },
    pressureUnit () {
      return useSettingsStore().getPressureUnit;
    },
    flowUnit () {
      return useSettingsStore().getFlowUnit;
    },
    title () {
      return this.isPressure ? 'Pressure Differential' : 'Flow Differential';
    }
  },
  methods: {
    filterPressure (pressure) {
      return (UnitConversion.FilterPressure(pressure, this.pressureUnit)).toFixed(2);
    },
    filterEmitterFlow (emitterFlow) {
      return (UnitConversion.FilterEmitterFlow(emitterFlow, this.flowUnit)).toFixed(2);
    }
  }
};
</script>

<style scoped>
.display {
  display: flex;
  flex-direction: row;
  height: 100%;
}
.display-box {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.top-row {
  border-left: 1px solid #999999;
  border-right: 1px solid #999999;
  border-top: 1px solid #999999;
  display: flex;
  flex: 1.5;
  flex-wrap: nowrap;
  text-align: center;
}
.bottom-row {
  border-bottom: 1px solid #999999;
  border-left: 1px solid #999999;
  border-right: 1px solid #999999;
  flex: 1;
  display: flex;
}
.top-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.top-middle {
  flex: 1;
  color: white;
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: center;
}
.top-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pressure-label {
  background-color: var(--pressure-color);
  border-radius: 25px;
  height: 100%;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flow-label {
  background-color: var(--flow-color);
  height: 100%;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bottom-cell {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  text-align: center;
  width: 33.33%;
}
.bottom-left {
  border-right: 1px solid #999999;
  border-top: 1px solid #999999;
}
.bottom-middle {
  border-top: 1px solid #999999;
}
.bottom-right {
  border-left: 1px solid #999999;
  border-top: 1px solid #999999;
}
.label {
  width: 40%;
  background-color: #DDDDDD;
  font-size: 60%;
}
.pressure-value {
  color: var(--pressure-color);
  font-weight: bold;
  width: 60%;
  font-size: 78%;
}
.flow-value {
  color: var(--flow-color);
  font-weight: bold;
  width: 60%;
  font-size: 78%;
}
.units-label {
  margin: auto 3px 3px 3px;
  width: 10%;
}
.flow-units-label {
  text-align: right;
  font-size: 70%;
  font-weight: bold;
}
.pressure-units-label {
  text-align: left;
  font-size: 70%;
  font-weight: bold;
}
.top-middle-unit {
  color: black;
  font-weight: 900;
}
.pressure-color {
  color: var(--pressure-color);
}
.flow-color {
  color: var(--flow-color);
}
</style>